<template>
  <div class="scenarios-table bg-white-1 shadow3 q-px-lg q-pt-md q-pb-lg">
    <AgGrid
      v-if="apiData"
      :options="gridOptions"
      :row-data="rowData"
      @cell-value-changed="cellValueChanged"
      @cell-clicked="cellClicked"
      @ready="agGrid = $event"
    >
      <template #header>
        <Grid gaf="column" justify-content="space-between" class="q-pb-lg">
          <Grid class="text-secondary-3 q-ml-sm">
            <div class="text-heading-3">Scenario table</div>
            <div class="text-secondary-1 text-body-1">
              Make scenarios by entering different budgets and costs. Changes made here will not affect the actual
              forecasts or budgets.
            </div>
          </Grid>

          <RBtn type="text" size="small" to="/departments">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M11.0325 11.9101L8.1225 9.00011L11.0325 6.09011C11.325 5.79761 11.325 5.32511 11.0325 5.03261C10.74 4.74011 10.2675 4.74011 9.975 5.03261L6.5325 8.47511C6.24 8.76761 6.24 9.24011 6.5325 9.53261L9.975 12.9751C10.2675 13.2676 10.74 13.2676 11.0325 12.9751C11.3175 12.6826 11.325 12.2026 11.0325 11.9101Z"
                fill="currentColor"
              />
            </svg>

            set actual budget
          </RBtn>
        </Grid>
      </template>
    </AgGrid>
  </div>
</template>

<script>
import * as apiTo from '@/modular-api'
// Mixins
import gridOptions from './gridOptions'
// Libraries
import { mapState, mapGetters, mapMutations } from 'vuex'
import { cloneDeep } from 'lodash'
// Components
import AgGrid from '@/ag-grid/AgGrid'

export default {
  components: {
    AgGrid
  },

  mixins: [gridOptions],

  data: () => ({
    agGrid: null,
    apiData: null,
    clonedData: null
  }),

  computed: {
    ...mapState('predictions', ['financialPeriodId']),
    ...mapGetters('scenarios', ['selectedDepartmentId']),
    ...mapGetters('entity-labels', ['departmentLabel']),

    pinnedRow() {
      if (!this.apiData) return null
      const totalData = this.apiData.total
      return {
        id: 'total',
        name: 'Total',
        budget: totalData.total_budget,
        cost: totalData.total_cost,
        following_year_budget_scenario_percentage: totalData.total_budget_scenario_percentage,
        following_year_cost_scenario_percentage: totalData.total_cost_scenario_percentage,
        following_year_budget_scenario_amount: totalData.total_budget_scenario_amount,
        following_year_cost_scenario_amount: totalData.total_cost_scenario_amount,
        difference_between_budget_and_cost_scenarios: totalData.total_difference,
        currency: totalData.currency
      }
    },

    rowData() {
      if (!this.apiData) return null

      return this.apiData.departments
    }
  },

  watch: {
    financialPeriodId: {
      handler: 'fetchData',
      immediate: true
    },

    pinnedRow(newVal, oldVal) {
      if (!oldVal) return
      this.agGrid.api.setPinnedTopRowData([newVal])
    },

    selectedDepartmentId(newVal) {
      const oldActive = document.querySelector(".ag-cell[col-id='name'].active")
      const newActive = document.querySelector(`.dep-id${newVal}`)
      if (newActive) {
        oldActive.classList.remove('active')
        newActive.classList.add('active')
      }
    }
  },

  methods: {
    ...mapMutations('scenarios', ['setDepartmentId', 'refreshCards']),

    fetchData(id) {
      if (!id) return
      apiTo.GET_BUDGET_COST_SCENARIOS_FOR_FP(id).then(({ data }) => {
        this.apiData = data.result
        this.clonedData = cloneDeep(data.result)
        this.updateHeaderNames(data.result.column_years)
      })
    },

    updateHeaderNames({ current: currentYear, following: nextYear }) {
      if (this.agGrid) {
        const update = (field, name) => (this.agGrid.api.getColumnDef(field).headerName = name)

        update('budget', `${currentYear} budget`)
        update('following_year_budget_scenario_percentage', `${nextYear} BUDGET SCENARIO %`)
        update('following_year_budget_scenario_amount', `${nextYear} BUDGET SCENARIO`)
        update('cost', `${currentYear} COST`)
        update('following_year_cost_scenario_percentage', `${nextYear} FORECAST SCENARIO %`)
        update('following_year_cost_scenario_amount', `${nextYear} SCENARIO COST`)
        this.agGrid.api.refreshHeader()
      }
    },

    didValueChanged(old, newVal) {
      if (typeof old === 'string' && typeof newVal === 'number') return Number(old) !== Number(newVal)
      return old !== newVal
    },

    getApiFromColId(colId) {
      const apis = {
        following_year_budget_scenario_percentage: apiTo.UPDATE_BUDGET_SCENARIO_PERCENTAGE_FOR_FP,
        following_year_cost_scenario_percentage: apiTo.UPDATE_COST_SCENARIO_PERCENTAGE_FOR_FP,
        following_year_budget_scenario_amount: apiTo.UPDATE_BUDGET_SCENARIO_AMOUNT_FOR_FP,
        following_year_cost_scenario_amount: apiTo.UPDATE_COST_SCENARIO_AMOUNT_FOR_FP
      }

      return apis[colId]
    },

    cellValueChanged(event) {
      if (!this.didValueChanged(event.oldValue, event.newValue)) return // no change happened
      const colId = event.column.colId
      const depId = event.data.id

      const api = this.getApiFromColId(colId)

      api(depId, this.financialPeriodId, { [colId]: event.newValue })
        .then(({ data }) => {
          this.apiData = data.result
          this.clonedData = cloneDeep(data.result)
          if (event.data.id !== this.selectedDepartmentId) this.setDepartmentId(event.data.id)
          this.refreshCards()
        })
        .catch(() => (this.apiData = cloneDeep(this.clonedData)))
    },

    cellClicked(params) {
      if (params.column.colId !== 'name' || params.data.id === 'total') return
      this.setDepartmentId(params.data.id)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/shadows';
@import '~@/styles/fonts';

.scenarios-table {
  grid-area: table;
  height: 526px;

  ::v-deep .ag-theme-alpine {
    .ag-body-viewport {
      box-shadow: none !important;
    }

    .ag-header-row {
      border-bottom: 1px solid var(--r-white-5);
    }

    .ag-cell[col-id='name']:not(.total) {
      text-transform: uppercase;
      cursor: pointer;
      color: var(--r-secondary-1);

      &:hover {
        background: var(--r-secondary-1);
        color: var(--r-white-1);
        @extend .text-body-2;
        line-height: 51px;
      }

      &:active {
        background: var(--r-secondary-2);
        color: var(--r-white-1);
        @extend .text-body-2;
        line-height: 51px;
      }

      &.active {
        @extend .text-body-2;
        line-height: 51px;
      }
    }

    .ag-floating-top {
      overflow-y: hidden !important;
      @extend .shadow5;
      z-index: 1;

      .ag-floating-top-container {
        border-bottom: 1px solid var(--r-white-4);
      }

      .ag-row {
        border-bottom: none;
      }

      .ag-cell {
        @extend .text-body-3;
        line-height: 51px;
      }
    }

    .ag-header-cell[aria-colindex='4'],
    .ag-header-cell[aria-colindex='5'],
    .ag-header-cell[aria-colindex='7'],
    .ag-header-cell[aria-colindex='8'] {
      .ag-header-cell-text {
        color: var(--r-secondary-1);
      }
    }
  }
}
</style>
